/**
 * DarvasBox — Darvas Box Scanner Dashboard
 *
 * Displays accumulation / distribution classification using the extended Darvas method.
 * Tabs: Overview, Breakout Ready, Accumulation, Avoid
 * V2 optimized: position near zone HIGH outperforms (walk-forward validated).
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, CircularProgress, alpha, Chip, Tooltip, Button, Skeleton, Drawer, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  TextField, InputAdornment,
  IconButton, Select, MenuItem,
} from '@mui/material';
import {
  Refresh, Close, TrendingUp, TrendingDown, Warning,
  ShowChart, Speed, Flag,
  Search,
  ShoppingCart, CandlestickChart, BarChart,
  InfoOutlined, Visibility,
} from '@mui/icons-material';

// ═══════════════════════════════════════════════════════════════
// DESIGN TOKENS
// ═══════════════════════════════════════════════════════════════

const LABEL_COLORS = {
  SIDEWAY_TICH_LUY: { bg: '#ecfdf5', color: '#10b981', border: '#6ee7b7' },
  SIDEWAY_TRUNG_TINH: { bg: '#eff6ff', color: '#3b82f6', border: '#93c5fd' },
  EXTENDED: { bg: '#fff1f2', color: '#e11d48', border: '#fda4af' },
  SIDEWAY_PHAN_PHOI: { bg: '#fffbeb', color: '#f59e0b', border: '#fcd34d' },
  DOWNTREND_TRA_HINH: { bg: '#fef2f2', color: '#ef4444', border: '#fca5a5' },
  DOWNTREND: { bg: '#fef2f2', color: '#ef4444', border: '#fca5a5' },
  LIQUIDITY_TOO_LOW: { bg: '#f8fafc', color: '#94a3b8', border: '#e2e8f0' },
};

const ACTION_COLORS = {
  UU_TIEN_BREAKOUT_READY: '#10b981',
  MOMENTUM_ZONE_QUAN_SAT: '#3b82f6',
  CHO_GIA_LEN_MOMENTUM_ZONE: '#3b82f6',
  CHI_NEN_QUAN_SAT: '#3b82f6',
  HOI_PHUC_CHO_NEN_MOI: '#0891b2',
  MUA_GAN_DAY_HOP: '#059669',
  CHO_BREAKOUT: '#10b981',
  QUAN_SAT_TRONG_HOP: '#3b82f6',
  QUAN_SAT_CHO_RS: '#0891b2',
  CHO_BREAKOUT_XAC_NHAN_RS: '#0891b2',
  MANH_NHUNG_CHUA_CO_HOP: '#6366f1',
  DA_TANG_NONG_NE_DU_DINH: '#e11d48',
  NE_TRANH: '#f59e0b',
  NE_TRANH_MUA_CANH_DUOI: '#f59e0b',
  BO_QUA_KHONG_BAT_DAY: '#ef4444',
  XU_HUONG_GIAM_RO_RANG: '#ef4444',
  BO_QUA_THANH_KHOAN_THAP: '#94a3b8',
  QUAN_SAT_CHO_SETUP: '#3b82f6',
};

const thSx = { fontSize: '0.68rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.04em', textTransform: 'uppercase', py: 1, px: 1.5, borderBottom: '2px solid #e2e8f0', bgcolor: '#f8fafc', whiteSpace: 'nowrap', lineHeight: 1.4 };
const tdSx = { py: 0.7, fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' };

// ── Risk/Reward ratio (prefer backend-computed, fallback to client calc) ──
const calcRR = (s) => {
  if (s.rr_t1 != null) return s.rr_t1;
  if (!s.last_close || !s.stop_loss || !s.target_1) return null;
  const risk = s.last_close - s.stop_loss;
  const reward = s.target_1 - s.last_close;
  if (risk <= 0) return null;
  return reward / risk;
};

// ── Stat Tile ────────────────────────────────────────────────
const Tile = ({ label, value, sub, color, Icon }) => (
  <Box sx={{
    position: 'relative', overflow: 'hidden',
    borderRadius: 1.5, border: `1px solid ${alpha(color, 0.15)}`,
    bgcolor: '#fff', p: '14px 16px', transition: 'all 0.2s',
    '&:hover': { borderColor: alpha(color, 0.3), boxShadow: `0 4px 16px ${alpha(color, 0.08)}`, transform: 'translateY(-1px)' },
  }}>
    <Box sx={{ position: 'absolute', top: 0, right: 0, width: 64, height: 64, background: `radial-gradient(circle at top right, ${alpha(color, 0.06)} 0%, transparent 70%)` }} />
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box sx={{
        width: 38, height: 38, borderRadius: 2, flexShrink: 0,
        background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 3px 8px ${alpha(color, 0.25)}`
      }}>
        <Icon sx={{ fontSize: 20, color: '#fff' }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', lineHeight: 1, mb: 0.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '1.75rem', fontWeight: 900, color, lineHeight: 1.2, fontFamily: 'monospace' }}>
          {value}
        </Typography>
        {sub && <Typography sx={{ fontSize: '0.68rem', color: '#64748b', mt: 0.4 }}>{sub}</Typography>}
      </Box>
    </Box>
  </Box>
);

// ── Score Bar ────────────────────────────────────────────────
const SCORE_THRESHOLDS = [
  [12, 'darvasBox.score.veryGood'],
  [8, 'darvasBox.score.good'],
  [4, 'darvasBox.score.fair'],
  [0, 'darvasBox.score.average'],
  [-Infinity, 'darvasBox.score.weak'],
];
const getScoreLabel = (score, t) => {
  for (const [threshold, key] of SCORE_THRESHOLDS) {
    if (score >= threshold) return t(key);
  }
  return t(SCORE_THRESHOLDS[SCORE_THRESHOLDS.length - 1][1]);
};

const ScoreBar = ({ score, maxScore = 20, t }) => {
  const c = score >= 12 ? '#10b981' : score >= 8 ? '#3b82f6' : score >= 0 ? '#f59e0b' : '#ef4444';
  const label = getScoreLabel(score, t);
  return (
    <Tooltip title={`${score?.toFixed(1)} / ${maxScore}`} arrow placement="top">
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: c, cursor: 'default' }}>
        {label}
      </Typography>
    </Tooltip>
  );
};

// ── Zone Position Bar ────────────────────────────────────────
const ZONE_THRESHOLDS = [
  [0.90, 'darvasBox.zone.nearResistance'],
  [0.70, 'darvasBox.zone.upperZone'],
  [0.50, 'darvasBox.zone.midZone'],
  [0.25, 'darvasBox.zone.lowerZone'],
  [-Infinity, 'darvasBox.zone.nearSupport'],
];
const getZoneLabel = (pos, t) => {
  for (const [threshold, key] of ZONE_THRESHOLDS) {
    if (pos >= threshold) return t(key);
  }
  return t(ZONE_THRESHOLDS[ZONE_THRESHOLDS.length - 1][1]);
};

const ZoneBar = ({ position, t }) => {
  if (position == null) return <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>—</Typography>;
  const pct = Math.min((position * 100), 999).toFixed(0);
  const c = position >= 0.9 ? '#10b981' : position >= 0.7 ? '#3b82f6' : position >= 0.5 ? '#f59e0b' : '#94a3b8';
  const label = getZoneLabel(position, t);
  return (
    <Tooltip title={`${pct}%`} arrow placement="top">
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: c, cursor: 'default' }}>
        {label}
      </Typography>
    </Tooltip>
  );
};

// ── Compact label-value row ──────────────────────────────────
const DRow = ({ label, value, color, mono = true, bold }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.35 }}>
    <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500 }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.78rem', fontWeight: bold ? 800 : 700, color: color || '#1e293b', fontFamily: mono ? 'monospace' : 'inherit' }}>
      {value}
    </Typography>
  </Box>
);

/* Score component → human level (used for sub-scores: structure, rs, etc.) */
const subScoreLevel = (v) => {
  if (v >= 3) return { text: 'Rất tốt', color: '#10b981' };
  if (v >= 1) return { text: 'Tốt', color: '#3b82f6' };
  if (v >= 0) return { text: 'Trung bình', color: '#f59e0b' };
  if (v >= -2) return { text: 'Yếu', color: '#f97316' };
  return { text: 'Xấu', color: '#ef4444' };
};

/* Total score → human level */
const totalScoreLevel = (v) => {
  if (v >= 15) return { text: 'Xuất sắc', color: '#10b981' };
  if (v >= 10) return { text: 'Mạnh', color: '#3b82f6' };
  if (v >= 5) return { text: 'Khá', color: '#f59e0b' };
  if (v >= 0) return { text: 'Trung bình', color: '#f97316' };
  return { text: 'Yếu', color: '#ef4444' };
};

/* Zone position → human level */
const positionLevel = (v) => {
  if (v >= 0.9) return { text: 'Sát đỉnh hộp', color: '#10b981' };
  if (v >= 0.7) return { text: 'Vùng cao', color: '#3b82f6' };
  if (v >= 0.4) return { text: 'Giữa hộp', color: '#f59e0b' };
  return { text: 'Vùng thấp', color: '#f97316' };
};

/* Zone width → human level */
const widthLevel = (v) => {
  const pct = (v || 0) * 100;
  if (pct <= 5) return { text: 'Nén chặt', color: '#10b981' };
  if (pct <= 10) return { text: 'Hẹp', color: '#3b82f6' };
  if (pct <= 15) return { text: 'Bình thường', color: '#f59e0b' };
  return { text: 'Rộng', color: '#f97316' };
};

/* Level row */
const LevelRow = ({ label, levelText, levelColor }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.35 }}>
    <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500 }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: levelColor }}>{levelText}</Typography>
  </Box>
);

// ═══════════════════════════════════════════════════════════════
// DETAIL DRAWER (slide from right, like BehaviorLabels)
// ═══════════════════════════════════════════════════════════════

const DetailDrawer = ({ open, onClose, stock, t, i18n }) => {
  if (!stock) return null;

  const labelCfg = LABEL_COLORS[stock.darvas_label] || LABEL_COLORS.SIDEWAY_TRUNG_TINH;
  const actionColor = ACTION_COLORS[stock.darvas_action] || '#6366f1';
  const entry = stock.last_close;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 420 }, p: 0 } } }}
    >
      {/* ── Header ── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2.5, py: 1.5, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CandlestickChart sx={{ fontSize: 20, color: '#6366f1' }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 900, color: '#1e293b' }}>
                {stock.ticker}
              </Typography>
              <Chip
                label={t(`darvasBox.label.${stock.darvas_label}`)}
                size="small"
                sx={{ bgcolor: alpha(labelCfg.color, 0.12), color: labelCfg.color, fontWeight: 700, fontSize: '0.6rem', height: 20 }}
              />
            </Box>
            <Chip
              label={t(`darvasBox.action.${stock.darvas_action}`)}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '0.58rem', borderColor: actionColor, color: actionColor, height: 18, mt: 0.3 }}
            />
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
      </Box>

      {/* ── Content ── */}
      <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>

        {/* ── Trading Plan Card ── */}
        <Box sx={{ mb: 2, bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1.5, p: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <Flag sx={{ fontSize: 15, color: '#6366f1' }} />
            <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: '#6366f1' }}>{t('darvasBox.dialog.tradingPlan')}</Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <Box>
              <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>
                {t('darvasBox.dialog.entry')}
              </Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: '#1e293b', fontFamily: 'monospace', lineHeight: 1.3 }}>
                {entry?.toLocaleString()}
              </Typography>
              {stock.entry_zone_low && stock.entry_zone_high && (
                <Typography sx={{ fontSize: '0.55rem', color: '#64748b', fontWeight: 600 }}>
                  {Math.round(stock.entry_zone_low).toLocaleString()} – {Math.round(stock.entry_zone_high).toLocaleString()}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.58rem', color: '#ef4444', fontWeight: 700, textTransform: 'uppercase' }}>
                {t('darvasBox.dialog.stopLoss')}
              </Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: '#dc2626', fontFamily: 'monospace', lineHeight: 1.3 }}>
                {stock.stop_loss ? Math.round(stock.stop_loss).toLocaleString() : '—'}
              </Typography>
              {stock.risk_pct != null ? (
                <Typography sx={{ fontSize: '0.55rem', color: '#ef4444', fontWeight: 600 }}>
                  -{stock.risk_pct.toFixed(1)}% {t('darvasBox.dialog.maxRisk')}
                </Typography>
              ) : entry && stock.stop_loss ? (
                <Typography sx={{ fontSize: '0.55rem', color: '#ef4444', fontWeight: 600 }}>
                  {((stock.stop_loss - entry) / entry * 100).toFixed(1)}%
                </Typography>
              ) : null}
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.58rem', color: '#10b981', fontWeight: 700, textTransform: 'uppercase' }}>
                {t('darvasBox.dialog.target1')}
              </Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: '#059669', fontFamily: 'monospace', lineHeight: 1.3 }}>
                {stock.target_1 ? Math.round(stock.target_1).toLocaleString() : '—'}
              </Typography>
              {entry && stock.target_1 && (
                <Typography sx={{ fontSize: '0.55rem', color: '#10b981', fontWeight: 600 }}>
                  +{((stock.target_1 - entry) / entry * 100).toFixed(1)}%
                </Typography>
              )}
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.58rem', color: '#059669', fontWeight: 700, textTransform: 'uppercase' }}>
                {t('darvasBox.dialog.target2')}
              </Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: '#047857', fontFamily: 'monospace', lineHeight: 1.3 }}>
                {stock.target_2 ? Math.round(stock.target_2).toLocaleString() : '—'}
              </Typography>
              {entry && stock.target_2 && (
                <Typography sx={{ fontSize: '0.55rem', color: '#059669', fontWeight: 600 }}>
                  +{((stock.target_2 - entry) / entry * 100).toFixed(1)}%
                </Typography>
              )}
            </Box>
          </Box>
          {/* R/R Ratio */}
          {(() => {
            const rr = calcRR(stock);
            if (rr === null) return null;
            const rrColor = rr >= 2 ? '#059669' : rr >= 1 ? '#2563eb' : '#dc2626';
            const rrBg = rr >= 2 ? '#ecfdf5' : rr >= 1 ? '#eff6ff' : '#fef2f2';
            return (
              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, bgcolor: rrBg, borderRadius: 1.5, px: 1.5, py: 0.8, border: `1px solid ${alpha(rrColor, 0.2)}` }}>
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                  {t('darvasBox.dialog.riskReward')}
                </Typography>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 900, color: rrColor, fontFamily: 'monospace' }}>
                  1 : {rr.toFixed(1)}
                </Typography>
                <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600 }}>
                  {rr >= 2 ? '✓ Tốt' : rr >= 1 ? '~ Khá' : '✗ Kém'}
                </Typography>
              </Box>
            );
          })()}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* ── Score Breakdown ── */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
            <Speed sx={{ fontSize: 14, color: '#8b5cf6' }} />
            <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: '#8b5cf6' }}>{t('darvasBox.dialog.scoreBreakdown')}</Typography>
          </Box>
          {(() => { const l = totalScoreLevel(stock.total_score || 0); return <LevelRow label={t('darvasBox.column.score')} levelText={l.text} levelColor={l.color} />; })()}
          {(() => { const l = subScoreLevel(stock.structure_score || 0); return <LevelRow label={t('darvasBox.column.structure')} levelText={l.text} levelColor={l.color} />; })()}
          {(() => { const l = subScoreLevel(stock.rs_score || 0); return <LevelRow label={t('darvasBox.column.rs')} levelText={l.text} levelColor={l.color} />; })()}
          {(() => {
            const v = stock.distribution_score || 0;
            const l = v >= 0 ? { text: 'An toàn', color: '#10b981' } : v >= -2 ? { text: 'Cẩn trọng', color: '#f59e0b' } : { text: 'Phân phối mạnh', color: '#ef4444' };
            return <LevelRow label={t('darvasBox.column.distribution')} levelText={l.text} levelColor={l.color} />;
          })()}
          {(() => { const l = subScoreLevel(stock.compression_score || 0); return <LevelRow label={t('darvasBox.dialog.compression')} levelText={l.text} levelColor={l.color} />; })()}
          {(() => { const l = subScoreLevel(stock.volume_value_score || 0); return <LevelRow label={t('darvasBox.dialog.volumeValue')} levelText={l.text} levelColor={l.color} />; })()}
          {(() => { const l = subScoreLevel(stock.bounce_score || 0); return <LevelRow label={t('darvasBox.dialog.bounce')} levelText={l.text} levelColor={l.color} />; })()}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* ── Zone Info ── */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
            <ShowChart sx={{ fontSize: 14, color: '#3b82f6' }} />
            <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: '#3b82f6' }}>{t('darvasBox.dialog.zoneInfo')}</Typography>
          </Box>
          <DRow label={t('darvasBox.dialog.zoneHigh')} value={stock.zone_high?.toLocaleString() || '—'} />
          <DRow label={t('darvasBox.dialog.zoneLow')} value={stock.zone_low?.toLocaleString() || '—'} />
          {(() => { const l = widthLevel(stock.zone_width_pct); return <LevelRow label={t('darvasBox.column.zoneWidth')} levelText={l.text} levelColor={l.color} />; })()}
          {(() => { const l = positionLevel(stock.position_in_zone || 0); return <LevelRow label={t('darvasBox.column.position')} levelText={l.text} levelColor={l.color} />; })()}
          <DRow label={t('darvasBox.column.sector')} value={(i18n.language === 'vi' ? stock.sector_name_vi : stock.sector_name) || stock.sector || '—'} mono={false} />
          <DRow label={t('darvasBox.dialog.liquidity20d')} value={`${((stock.median_value_20 || 0) / 1e9).toFixed(1)}B`} />
        </Box>

        {/* ── Reasons ── */}
        {Array.isArray(stock.reasons) && stock.reasons.length > 0 && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Box>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', mb: 0.8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t('darvasBox.dialog.reasons')}
              </Typography>
              <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1.5, p: 1.5 }}>
                <Typography sx={{ fontSize: '0.68rem', color: '#475569', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                  {(() => {
                    const lang = i18n.language === 'vi' ? 'vi' : 'en';
                    return stock.reasons
                      .map(r => (typeof r === 'string' ? r : (r[lang] || r.vi || r.en || '')))
                      .filter(Boolean)
                      .map(r => `• ${r}`)
                      .join('\n');
                  })()}
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};


// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const DarvasBox = () => {
  const { t, i18n } = useTranslation();

  const [scores, setScores] = useState([]);
  const [summary, setSummary] = useState(null);
  const [breadth, setBreadth] = useState(null);
  const [dipBuy, setDipBuy] = useState(null);   // server-computed dip-buy recommendation
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('overview');
  const [selectedStock, setSelectedStock] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [searchTicker, setSearchTicker] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState('desc');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [scoresRes, summaryRes, breadthRes, dipBuyRes] = await Promise.all([
        fetch('/api/darvas-boxes/scores?limit=500'),
        fetch('/api/darvas-boxes/summary'),
        fetch('/api/darvas-boxes/market-breadth'),
        fetch('/api/darvas-boxes/dip-buy-picks?top_n=12'),
      ]);
      if (!scoresRes.ok || !summaryRes.ok) throw new Error('API error');

      const scoresData = await scoresRes.json();
      const summaryData = await summaryRes.json();
      const breadthData = breadthRes.ok ? await breadthRes.json() : null;
      const dipBuyData = dipBuyRes.ok ? await dipBuyRes.json() : null;

      setScores(scoresData.scores || []);
      setSummary(summaryData);
      setBreadth(breadthData);
      setDipBuy(dipBuyData);
    } catch (err) {
      console.error('DarvasBox load error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const triggerScan = useCallback(async () => {
    setScanning(true);
    try {
      const res = await fetch('/api/darvas-boxes/scan', { method: 'POST' });
      if (res.status === 409) return; // already running
      if (!res.ok) throw new Error('Scan failed');
      await load();
    } catch (err) {
      console.error('Scan error:', err);
      setError(err.message);
    } finally {
      setScanning(false);
    }
  }, [load]);

  // ── Derived data ───────────────────────────────────────────
  const dist = summary?.distribution || {};
  const totalScanned = summary?.total || 0;
  const tichLuyCount = dist['SIDEWAY_TICH_LUY'] || 0;
  const phanPhoiCount = dist['SIDEWAY_PHAN_PHOI'] || 0;
  const downtrendCount = (dist['DOWNTREND_TRA_HINH'] || 0) + (dist['DOWNTREND'] || 0);

  const breakoutReady = useMemo(() =>
    scores.filter(s => s.darvas_action === 'UU_TIEN_BREAKOUT_READY')
      .sort((a, b) => (b.breakout_watch_rank || 0) - (a.breakout_watch_rank || 0)),
    [scores]
  );

  // ► TOP BUY — cổ phiếu sắp breakout, đáng mua hôm nay (hero section)
  // Filter (calibrated on 89-week backtest, 2024-06 → 2026-02):
  //   - TICH_LUY (có hộp Darvas hợp lệ, structure + RS đủ mạnh)
  //   - position_in_zone ≥ 0.85 (giá sát đỉnh hộp, chuẩn bị phá kháng cự)
  //     → bucket này cho alpha 20d +2.6~3.9% (WR ~32%), vs middle/bottom âm alpha
  // Không dùng R/R làm gate: mã near-breakout có target gần nên R/R thấp theo
  // thiết kế — lọc R/R ≥ 1.5 sẽ bỏ đúng nhóm alpha cao nhất.
  // Sort: breakout_watch_rank giảm dần (mã sát đỉnh hộp nhất đứng đầu).
  const topBuyCandidates = useMemo(() => {
    return scores.filter(s => {
      if (s.darvas_label !== 'SIDEWAY_TICH_LUY') return false;
      const pos = s.position_in_zone;
      if (pos == null) return false;
      return pos >= 0.85;
    }).sort((a, b) =>
      (b.breakout_watch_rank || 0) - (a.breakout_watch_rank || 0)
    ).slice(0, 10);
  }, [scores]);

  // ► DIP BUY — mua đáy hộp
  // All filter/score/rank logic is computed server-side by
  // `GET /api/darvas-boxes/dip-buy-picks` and stored in `dipBuy` state.
  // Frontend here only renders the server response.
  const dipBuyCandidates = dipBuy?.picks || [];

  const accumulation = useMemo(() =>
    scores.filter(s => s.darvas_label === 'SIDEWAY_TICH_LUY')
      .sort((a, b) => (b.breakout_watch_rank || 0) - (a.breakout_watch_rank || 0)),
    [scores]
  );

  const avoidList = useMemo(() =>
    scores.filter(s => s.darvas_label === 'SIDEWAY_PHAN_PHOI' || s.darvas_label === 'DOWNTREND_TRA_HINH' || s.darvas_label === 'DOWNTREND')
      .sort((a, b) => (a.total_score || 0) - (b.total_score || 0)),
    [scores]
  );

  // Watchlist = tích lũy + position >= 70% (đang trong hộp Darvas, cải thiện dần, chuẩn bị breakout)
  const watchlistData = useMemo(() =>
    scores.filter(s =>
      s.darvas_label === 'SIDEWAY_TICH_LUY'
      && s.position_in_zone != null
      && s.position_in_zone >= 0.70
    ).sort((a, b) => (b.position_in_zone || 0) - (a.position_in_zone || 0)),
    [scores]
  );

  // Unique sectors for filter chips
  const sectorList = useMemo(() => {
    const map = new Map();
    scores.forEach(s => {
      const name = (i18n.language === 'vi' ? s.sector_name_vi : s.sector_name) || s.sector;
      if (name && name !== 'UNKNOWN') map.set(s.sector, name);
    });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [scores, i18n.language]);

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  };

  const displayData = useMemo(() => {
    let data;
    switch (tab) {
      case 'breakout': data = [...breakoutReady]; break;
      case 'accumulation': data = [...accumulation]; break;
      case 'avoid': data = [...avoidList]; break;
      case 'watchlist': data = [...watchlistData]; break;
      default: data = [...scores];
    }

    // Search filter
    if (searchTicker) {
      const q = searchTicker.toUpperCase();
      data = data.filter(s => s.ticker.includes(q));
    }

    // Sector filter
    if (sectorFilter) {
      data = data.filter(s => s.sector === sectorFilter);
    }

    // Sort
    if (sortCol) {
      data.sort((a, b) => {
        let va, vb;
        if (sortCol === 'rr') {
          va = calcRR(a) ?? -999;
          vb = calcRR(b) ?? -999;
        } else {
          va = a[sortCol] ?? -999;
          vb = b[sortCol] ?? -999;
        }
        return sortDir === 'asc' ? va - vb : vb - va;
      });
    }

    return data;
  }, [tab, scores, breakoutReady, accumulation, avoidList, watchlistData, searchTicker, sectorFilter, sortCol, sortDir]);

  // ── Render ─────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 1.5, md: 3 }, py: 2 }}>
        {/* Header skeleton */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: '12px' }} />
            <Box>
              <Skeleton width={180} height={24} />
              <Skeleton width={120} height={14} sx={{ mt: 0.3 }} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Skeleton variant="rounded" width={100} height={36} sx={{ borderRadius: '4px' }} />
            <Skeleton variant="rounded" width={80} height={36} sx={{ borderRadius: '4px' }} />
          </Box>
        </Box>

        {/* Stat Tiles skeleton */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 1.5, mb: 2.5 }}>
          {[0,1,2,3,4].map(i => (
            <Box key={i} sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', p: '14px 16px' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: 2, flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width={60} height={12} sx={{ mb: 0.5 }} />
                  <Skeleton width={40} height={32} />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Tabs skeleton */}
        <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
          {[90,100,110,85,75].map((w, i) => (
            <Skeleton key={i} variant="rounded" width={w} height={32} sx={{ borderRadius: 1 }} />
          ))}
        </Box>

        {/* Table skeleton */}
        <TableContainer sx={{ bgcolor: '#fff', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['#','Ticker','Label','Action','Score','Position','Zone','Close','Sector'].map(h => (
                  <TableCell key={h} sx={thSx}>
                    <Skeleton width={h === '#' ? 16 : h.length * 8} height={12} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(8)].map((_, i) => (
                <TableRow key={i} sx={{ bgcolor: i % 2 === 0 ? '#fff' : '#fafbfe' }}>
                  <TableCell sx={tdSx}><Skeleton width={16} /></TableCell>
                  <TableCell sx={tdSx}><Skeleton width={48} height={16} /></TableCell>
                  <TableCell sx={tdSx}><Skeleton variant="rounded" width={90} height={22} sx={{ borderRadius: 3 }} /></TableCell>
                  <TableCell sx={tdSx}><Skeleton variant="rounded" width={80} height={20} sx={{ borderRadius: 1.5 }} /></TableCell>
                  <TableCell sx={tdSx}><Skeleton variant="rounded" width={80} height={6} sx={{ borderRadius: 1 }} /></TableCell>
                  <TableCell sx={tdSx}><Skeleton variant="rounded" width={80} height={6} sx={{ borderRadius: 1 }} /></TableCell>
                  <TableCell sx={tdSx} align="right"><Skeleton width={40} /></TableCell>
                  <TableCell sx={tdSx} align="right"><Skeleton width={50} /></TableCell>
                  <TableCell sx={tdSx}><Skeleton width={70} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: 'calc(100vh - 160px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: { xs: 4, md: 8 },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 440,
            bgcolor: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
            p: { xs: 3, md: 4 },
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              mx: 'auto',
              mb: 2,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
              border: '1px solid #fed7aa',
            }}
          >
            <Warning sx={{ fontSize: 32, color: '#ea580c' }} />
          </Box>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', mb: 0.75 }}>
            {t('darvasBox.error.title')}
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.6, mb: 2.5 }}>
            {t('darvasBox.error.description')}
          </Typography>
          {error && typeof error === 'string' && (
            <Box
              sx={{
                bgcolor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 1.25,
                px: 1.5,
                py: 1,
                mb: 2.5,
                textAlign: 'left',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  color: '#94a3b8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  mb: 0.25,
                }}
              >
                {t('darvasBox.error.detailLabel')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#475569',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  wordBreak: 'break-word',
                }}
              >
                {error}
              </Typography>
            </Box>
          )}
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={load}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
              px: 2.5,
              py: 0.9,
              background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
              boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
              },
            }}
          >
            {t('darvasBox.error.retry')}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 1.5, md: 3 }, py: 2 }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5, flexShrink: 0,
            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)',
          }}>
            <CandlestickChart sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
                {t('darvasBox.title')}
              </Typography>
              {summary?.trade_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#6366f1', boxShadow: '0 0 6px #6366f1' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>{summary.trade_date}</Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {t('darvasBox.subtitle')}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={scanning ? <CircularProgress size={16} color="inherit" /> : <Refresh sx={{ fontSize: 16 }} />}
          onClick={scanning ? undefined : triggerScan}
          disabled={scanning}
          sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
            bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
        >
          {scanning ? t('darvasBox.scanning', 'Scanning...') : t('darvasBox.scanButton')}
        </Button>
      </Box>

      {/* ── Market Breadth Banner ── */}
      {breadth && (breadth.state === 'NARROW_RALLY' || breadth.state === 'NARROW_DECLINE') && (
        <Box sx={{
          mb: 2, p: 1.2, borderRadius: 1.5,
          bgcolor: breadth.state === 'NARROW_RALLY' ? '#fffbeb' : '#fef2f2',
          border: `1px solid ${breadth.state === 'NARROW_RALLY' ? '#fcd34d' : '#fecaca'}`,
          display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap',
        }}>
          <Warning sx={{ fontSize: 18, color: breadth.state === 'NARROW_RALLY' ? '#d97706' : '#dc2626' }} />
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: breadth.state === 'NARROW_RALLY' ? '#92400e' : '#991b1b' }}>
              {breadth.state === 'NARROW_RALLY'
                ? t('darvasBox.breadth.narrowRallyTitle', 'Thị trường nhiễu — chỉ nhóm vốn hoá lớn kéo VNINDEX')
                : t('darvasBox.breadth.narrowDeclineTitle', 'Thị trường nhiễu — vốn hoá lớn ghìm VNINDEX')}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: breadth.state === 'NARROW_RALLY' ? '#78350f' : '#7f1d1d', mt: 0.2 }}>
              {t('darvasBox.breadth.detail',
                'VNINDEX 20 phiên: {{vn}}% · Cổ phiếu trung vị: {{peer}}% · Chênh: {{excess}}%. RS đã chuẩn hoá theo ngang hàng (peer median) thay vì VNINDEX.',
                {
                  vn: ((breadth.vnindex_ret_20 ?? 0) * 100).toFixed(1),
                  peer: ((breadth.peer_median_ret_20 ?? 0) * 100).toFixed(1),
                  excess: ((breadth.excess_vnindex ?? 0) * 100).toFixed(1),
                })}
            </Typography>
          </Box>
          <Chip
            label={`${t('darvasBox.breadth.benchmark', 'RS benchmark')}: ${t(`darvasBox.breadth.benchmarkValue.${breadth.benchmark_used || 'VNINDEX'}`, breadth.benchmark_used || 'VNINDEX')}`}
            size="small"
            sx={{ bgcolor: '#fff', border: '1px solid #d1d5db', fontWeight: 700, fontSize: '0.65rem', height: 22 }}
          />
        </Box>
      )}

      {/* ── Stat Tiles ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 1.5, mb: 2.5 }}>
        <Tile label={t('darvasBox.stats.total')} value={totalScanned} color="#3b82f6" Icon={BarChart} />
        <Tile label={t('darvasBox.stats.accumulation')} value={tichLuyCount} color="#10b981" Icon={TrendingUp} />
        <Tile label={t('darvasBox.stats.breakoutReady')} value={breakoutReady.length} color="#6366f1" Icon={ShoppingCart} />
          <Tile label={t('darvasBox.stats.distribution')} value={phanPhoiCount} color="#f59e0b" Icon={Warning} />
        <Tile label={t('darvasBox.stats.downtrend')} value={downtrendCount} color="#ef4444" Icon={TrendingDown} />
      </Box>

      {/* ── Hero #1: DIP BUY — mua đáy hộp của mã mạnh (pullback) ──
          All regime detection, filtering, R/R computation and ranking is
          performed server-side at GET /api/darvas-boxes/dip-buy-picks.
          The frontend only renders the response. */}
      {dipBuyCandidates.length > 0 && (() => {
        const regime = dipBuy?.regime || 'unknown';
        const vnRet20 = dipBuy?.vnindex_ret_20_pct ?? null;
        const regimeLabel = regime === 'strong'
          ? t('darvasBox.dipBuy.regime.strong', 'Chỉ số mạnh — ưu tiên laggards')
          : regime === 'moderate'
          ? t('darvasBox.dipBuy.regime.moderate', 'Chỉ số trung tính — ưu tiên leaders')
          : regime === 'weak'
          ? t('darvasBox.dipBuy.regime.weak', 'Chỉ số yếu — chỉ leaders thật')
          : t('darvasBox.dipBuy.regime.unknown', 'Chưa xác định regime');
        const disclaimer = regime === 'strong'
          ? t('darvasBox.dipBuy.disclaimer.strong',
              'Backtest 89 tuần, regime "chỉ số mạnh" (VNINDEX 20p ≥ 6%): mã laggard ở đáy hộp có EV tuyệt đối +8.24% (banks +15.78%), stop-hit 0% — tide nâng thuyền, mã chưa chạy hồi mạnh nhất. Alpha có thể âm (lag index) nhưng P/L dương. Cắt lỗ NGAY nếu đóng cửa dưới đáy hộp.')
          : regime === 'moderate'
          ? t('darvasBox.dipBuy.disclaimer.moderate',
              'Backtest regime "trung tính": leaders (RS≥0) ở đáy hộp có EV +7.6%, laggards chỉ +0.3%. Ưu tiên mã RS≥2 nếu có. Cắt lỗ khi đóng cửa dưới đáy hộp.')
          : regime === 'weak'
          ? t('darvasBox.dipBuy.disclaimer.weak',
              'CẢNH BÁO regime "chỉ số yếu": backtest cho thấy tất cả setup pos≤0.30 đều EV âm. Chỉ nên chờ leaders thật (RS≥2) hoặc ngồi ngoài. Không bắt đáy trong downtrend.')
          : t('darvasBox.dipBuy.disclaimer.unknown',
              'Regime chưa xác định. Mua rải quanh đáy hộp; cắt lỗ nếu đóng cửa DƯỚI đáy hộp.');
        const borderColor = regime === 'weak' ? '#fca5a5' : '#6ee7b7';
        const bgGradient = regime === 'weak'
          ? 'linear-gradient(135deg, #fef2f2 0%, #fff7ed 100%)'
          : 'linear-gradient(135deg, #ecfdf5 0%, #f0fdfa 100%)';
        return (
        <Box sx={{
          mb: 2.5, p: { xs: 1.5, md: 2 }, borderRadius: 2,
          background: bgGradient,
          border: `1.5px solid ${borderColor}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box sx={{
              width: 28, height: 28, borderRadius: 1, bgcolor: '#10b981',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
            }}>
              <ShoppingCart sx={{ fontSize: 16, color: '#fff' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#065f46', letterSpacing: -0.2 }}>
                {t('darvasBox.dipBuy.title', 'Cơ hội đáy hộp')}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#047857', fontWeight: 500 }}>
                {regimeLabel}
                {vnRet20 != null && (
                  <Box component="span" sx={{ ml: 0.8, color: '#065f46', fontWeight: 700 }}>
                    · VNINDEX 20p: {vnRet20 >= 0 ? '+' : ''}{vnRet20.toFixed(1)}%
                  </Box>
                )}
              </Typography>
            </Box>
            <Chip label={`${dipBuyCandidates.length} ${t('darvasBox.dipBuy.picks', 'cơ hội')}`} size="small"
              sx={{ bgcolor: '#10b981', color: '#fff', fontWeight: 700, fontSize: '0.68rem', height: 22 }} />
          </Box>

          <Box sx={{
            mb: 1.2, p: 1, borderRadius: 1,
            bgcolor: regime === 'weak' ? '#fef2f2' : '#d1fae5',
            border: `1px solid ${regime === 'weak' ? '#fca5a5' : '#6ee7b7'}`,
            display: 'flex', alignItems: 'flex-start', gap: 0.8,
          }}>
            <InfoOutlined sx={{ fontSize: 14, color: regime === 'weak' ? '#991b1b' : '#047857', mt: 0.1 }} />
            <Typography sx={{ fontSize: '0.68rem', color: regime === 'weak' ? '#991b1b' : '#065f46', fontWeight: 500, lineHeight: 1.4 }}>
              {disclaimer}
            </Typography>
          </Box>

          <TableContainer sx={{ bgcolor: '#fff', borderRadius: 1.5, border: '1px solid #d1fae5' }}>
            <Table size="small" sx={{ '& td, & th': { py: 0.6, px: 1, borderColor: '#ecfdf5' } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f0fdf4' }}>
                  <TableCell sx={{ fontWeight: 800, fontSize: '0.66rem', color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {t('darvasBox.topBuy.col.ticker', 'Mã')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: '0.66rem', color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {t('darvasBox.topBuy.col.sector', 'Ngành')}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 800, fontSize: '0.66rem', color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    <Tooltip title={t('darvasBox.dipBuy.rsHelp', 'Sức mạnh tương đối so với peer median (20 phiên): >0 là leader, <0 là laggard')}>
                      <Box component="span" sx={{ borderBottom: '1px dotted #065f46', cursor: 'help' }}>
                        {t('darvasBox.dipBuy.col.strength', 'Sức mạnh')}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 800, fontSize: '0.66rem', color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    <Tooltip title={t('darvasBox.dipBuy.posHelp', 'Vị trí giá hiện tại trong hộp: 0% = sát đáy (rẻ), 100% = sát đỉnh')}>
                      <Box component="span" sx={{ borderBottom: '1px dotted #065f46', cursor: 'help' }}>
                        {t('darvasBox.dipBuy.col.location', 'Vị trí trong hộp')}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.66rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {t('darvasBox.topBuy.col.last', 'Giá HT')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.66rem', color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {t('darvasBox.dipBuy.col.limit', 'Lệnh chờ')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.66rem', color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {t('darvasBox.topBuy.stop', 'Cắt lỗ')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.66rem', color: '#059669', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {t('darvasBox.dipBuy.col.target', 'Đỉnh hộp')}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 800, fontSize: '0.66rem', color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    <Tooltip title={t('darvasBox.dipBuy.rrHelp', 'Tỉ lệ Lời/Lỗ: kỳ vọng lời chia cho mức cắt lỗ. ≥1.5 nghĩa là được 1.5đ lời đổi 1đ rủi ro.')}>
                      <Box component="span" sx={{ borderBottom: '1px dotted #065f46', cursor: 'help' }}>
                        {t('darvasBox.dipBuy.col.rr', 'Lời/Lỗ')}
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dipBuyCandidates.map((s) => {
                  // All values below are pre-computed by the backend
                  // (/api/darvas-boxes/dip-buy-picks).
                  const entry = s.limit_entry;         // = zone_low (limit order)
                  const currentPrice = s.last_close;
                  const stop = s.stop_loss;
                  const target = s.zone_high;
                  const riskPct = s.risk_pct;
                  const rewardPct = s.reward_pct;
                  const rr = s.rr;
                  const distToEntry = s.dist_to_entry_pct;
                  const sectorName = (i18n.language === 'vi' ? s.sector_name_vi : s.sector_name) || s.sector || '—';

                  return (
                    <TableRow
                      key={s.ticker}
                      hover
                      onClick={() => setSelectedStock(s)}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f0fdf4 !important' } }}
                    >
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.88rem', fontWeight: 900, color: '#1e293b' }}>
                        {s.ticker}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', color: '#64748b', maxWidth: 180, lineHeight: 1.3 }}>
                        <Tooltip title={sectorName}>
                          <Box component="span">{sectorName}</Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        {(() => {
                          // Backend classifies into enum; frontend only maps to i18n + style.
                          const STRENGTH_STYLE = {
                            leader:  { bg: '#10b981', fg: '#fff' },
                            neutral: { bg: '#bbf7d0', fg: '#065f46' },
                            laggard: { bg: '#fde68a', fg: '#92400e' },
                          };
                          const key = s.strength_label || 'neutral';
                          const style = STRENGTH_STYLE[key] || STRENGTH_STYLE.neutral;
                          const rsRaw = Number(s.rs_score || 0);
                          return (
                            <Tooltip title={`${t('darvasBox.dipBuy.rsHelp', 'Sức mạnh tương đối so với peer median (20 phiên): >0 là leader, <0 là laggard')} (RS: ${rsRaw > 0 ? '+' : ''}${rsRaw.toFixed(0)})`}>
                              <Chip
                                label={t(`darvasBox.dipBuy.rs.${key}`)}
                                size="small"
                                sx={{
                                  bgcolor: style.bg, color: style.fg,
                                  fontWeight: 700, fontSize: '0.62rem', height: 20, borderRadius: 1, px: 0.4,
                                }}
                              />
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="center">
                        {(() => {
                          const POS_STYLE = {
                            bottom: { fg: '#047857', bar: '#10b981' },
                            middle: { fg: '#92400e', bar: '#f59e0b' },
                            top:    { fg: '#9a3412', bar: '#ef4444' },
                          };
                          const key = s.position_label || 'middle';
                          const style = POS_STYLE[key] || POS_STYLE.middle;
                          // Bar width is a visual-only mapping of raw pos; no thresholds recomputed.
                          const pos = Math.round((s.position_in_zone ?? 0) * 100);
                          return (
                            <Tooltip title={t('darvasBox.dipBuy.posHelp', 'Vị trí giá trong hộp: 0% = sát đáy (rẻ), 100% = sát đỉnh')}>
                              <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.3, minWidth: 66 }}>
                                <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: style.fg, lineHeight: 1 }}>
                                  {t(`darvasBox.dipBuy.pos.${key}`)}
                                </Typography>
                                <Box sx={{ position: 'relative', width: 60, height: 6, bgcolor: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                                  <Box sx={{
                                    position: 'absolute', top: 0, bottom: 0,
                                    left: 0, width: `${Math.max(3, pos)}%`,
                                    bgcolor: style.bar,
                                    borderRadius: 3,
                                  }} />
                                </Box>
                              </Box>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.76rem', fontWeight: 500, color: '#64748b' }}>
                        {currentPrice != null ? Math.round(currentPrice).toLocaleString('vi-VN') : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 700, color: '#1e293b' }}>
                        {entry != null ? Math.round(entry).toLocaleString('vi-VN') : '—'}
                        {s.entry_status && s.entry_status !== 'unknown' && (
                          <Typography component="span" sx={{
                            ml: 0.4, fontSize: '0.62rem',
                            color: s.entry_status === 'waiting' ? '#d97706' : '#10b981',
                            fontWeight: 600,
                          }}>
                            {s.entry_status === 'waiting'
                              ? t('darvasBox.dipBuy.entry.waiting', { defaultValue: '(chờ −{{pct}}%)', pct: distToEntry?.toFixed(1) })
                              : s.entry_status === 'below'
                              ? t('darvasBox.dipBuy.entry.below', '(đã dưới)')
                              : t('darvasBox.dipBuy.entry.ready', '(sẵn sàng)')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 700, color: '#dc2626' }}>
                        {stop != null ? Math.round(stop).toLocaleString('vi-VN') : '—'}
                        {riskPct != null && (
                          <Typography component="span" sx={{ ml: 0.4, fontSize: '0.62rem', color: '#ef4444', fontWeight: 600 }}>
                            (−{riskPct.toFixed(1)}%)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 700, color: '#059669' }}>
                        {target != null ? Math.round(target).toLocaleString('vi-VN') : '—'}
                        {rewardPct != null && (
                          <Typography component="span" sx={{ ml: 0.4, fontSize: '0.62rem', color: '#10b981', fontWeight: 600 }}>
                            (+{rewardPct.toFixed(1)}%)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {rr != null ? (
                          <Chip
                            label={`1 : ${rr.toFixed(1)}`}
                            size="small"
                            sx={{
                              bgcolor: rr >= 2 ? '#10b981' : '#3b82f6',
                              color: '#fff', fontWeight: 800, fontSize: '0.66rem', height: 20, borderRadius: 1, minWidth: 54,
                            }}
                          />
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        );
      })()}

      {/* ── Hero #2: WATCHLIST — chờ xác nhận breakout ── */}
      {topBuyCandidates.length > 0 && (
        <Box sx={{
          mb: 2.5, p: { xs: 1.5, md: 2 }, borderRadius: 2,
          background: 'linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%)',
          border: '1.5px solid #93c5fd',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box sx={{
              width: 28, height: 28, borderRadius: 1, bgcolor: '#3b82f6',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
            }}>
              <Visibility sx={{ fontSize: 16, color: '#fff' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#1e3a8a', letterSpacing: -0.2 }}>
                {t('darvasBox.topBuy.title', 'Watchlist chờ breakout')}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#1e40af', fontWeight: 500 }}>
                {t('darvasBox.topBuy.subtitle', 'Cổ phiếu sát đỉnh hộp — chỉ vào lệnh khi giá đóng cửa VƯỢT kháng cự kèm volume tăng')}
              </Typography>
            </Box>
            <Chip label={`${topBuyCandidates.length} ${t('darvasBox.topBuy.picks', 'mã theo dõi')}`} size="small"
              sx={{ bgcolor: '#3b82f6', color: '#fff', fontWeight: 700, fontSize: '0.68rem', height: 22 }} />
          </Box>

          {/* Honest-disclosure banner */}
          <Box sx={{
            mb: 1.2, p: 1, borderRadius: 1,
            bgcolor: '#fef3c7', border: '1px solid #fcd34d',
            display: 'flex', alignItems: 'flex-start', gap: 0.8,
          }}>
            <Warning sx={{ fontSize: 14, color: '#b45309', mt: 0.1 }} />
            <Typography sx={{ fontSize: '0.68rem', color: '#78350f', fontWeight: 500, lineHeight: 1.4 }}>
              {t('darvasBox.topBuy.disclaimer', 'Thống kê backtest 89 tuần: ~32% số mã ở vị trí này phá đỉnh thành công. KHÔNG mua sớm khi giá còn trong hộp. Chỉ vào lệnh sau khi giá đóng cửa trên giá kích hoạt với volume tăng ≥ 1.5× trung bình.')}
            </Typography>
          </Box>

          <TableContainer sx={{ bgcolor: '#fff', borderRadius: 1.5, border: '1px solid #dbeafe' }}>
            <Table size="small" sx={{ '& td, & th': { py: 0.6, px: 1, borderColor: '#eff6ff' } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#eff6ff' }}>
                  <TableCell sx={{ fontWeight: 800, fontSize: '0.66rem', color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {t('darvasBox.topBuy.col.ticker', 'Mã')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: '0.66rem', color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {t('darvasBox.topBuy.col.sector', 'Ngành')}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 800, fontSize: '0.66rem', color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {t('darvasBox.topBuy.col.zone', 'Vị trí')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.66rem', color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {t('darvasBox.topBuy.col.last', 'Giá HT')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.66rem', color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {t('darvasBox.topBuy.col.trigger', 'Giá kích hoạt')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.66rem', color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {t('darvasBox.topBuy.stop', 'Cắt lỗ')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.66rem', color: '#059669', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {t('darvasBox.topBuy.target', 'Mục tiêu')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: '0.66rem', color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {t('darvasBox.topBuy.col.reason', 'Lý do')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topBuyCandidates.map((s) => {
                  const lastClose = s.last_close;
                  const trigger = s.zone_high;        // breakout trigger = box resistance
                  const stop = s.stop_loss;
                  const target = s.target_1;
                  const triggerGapPct = trigger && lastClose ? ((trigger - lastClose) / lastClose * 100) : null;
                  const riskPct = stop && trigger ? ((trigger - stop) / trigger * 100) : null;
                  const rewardPct = target && trigger ? ((target - trigger) / trigger * 100) : null;
                  const sectorName = (i18n.language === 'vi' ? s.sector_name_vi : s.sector_name) || s.sector || '—';

                  return (
                    <TableRow
                      key={s.ticker}
                      hover
                      onClick={() => setSelectedStock(s)}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#eff6ff !important' } }}
                    >
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.88rem', fontWeight: 900, color: '#1e293b' }}>
                        {s.ticker}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', color: '#64748b', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {sectorName}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${Math.round((s.position_in_zone ?? 0) * 100)}%`}
                          size="small"
                          sx={{
                            bgcolor: '#dbeafe', color: '#1e40af',
                            fontWeight: 700, fontSize: '0.62rem', height: 18, borderRadius: 1, minWidth: 38,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 700, color: '#475569' }}>
                        {lastClose != null ? Math.round(lastClose).toLocaleString('vi-VN') : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 800, color: '#7c3aed' }}>
                        {trigger != null ? Math.round(trigger).toLocaleString('vi-VN') : '—'}
                        {triggerGapPct != null && triggerGapPct > 0 && (
                          <Typography component="span" sx={{ ml: 0.4, fontSize: '0.62rem', color: '#a78bfa', fontWeight: 600 }}>
                            (+{triggerGapPct.toFixed(1)}%)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 700, color: '#dc2626' }}>
                        {stop != null ? Math.round(stop).toLocaleString('vi-VN') : '—'}
                        {riskPct != null && (
                          <Typography component="span" sx={{ ml: 0.4, fontSize: '0.62rem', color: '#ef4444', fontWeight: 600 }}>
                            (−{riskPct.toFixed(1)}%)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 700, color: '#059669' }}>
                        {target != null ? Math.round(target).toLocaleString('vi-VN') : '—'}
                        {rewardPct != null && (
                          <Typography component="span" sx={{ ml: 0.4, fontSize: '0.62rem', color: '#10b981', fontWeight: 600 }}>
                            (+{rewardPct.toFixed(1)}%)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', color: '#475569', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {(() => {
                          const lang = i18n.language === 'vi' ? 'vi' : 'en';
                          const raw = Array.isArray(s.reasons) ? s.reasons : [];
                          const list = raw.map(r => (typeof r === 'string' ? r : (r[lang] || r.vi || r.en || ''))).filter(Boolean);
                          const first = list[0] || '';
                          if (list.length <= 1) return first;
                          return (
                            <Tooltip title={<Box sx={{ whiteSpace: 'pre-line', fontSize: '0.72rem' }}>{list.join('\n')}</Box>} placement="left" arrow>
                              <span>{first} <span style={{ color: '#94a3b8', fontWeight: 700 }}>+{list.length - 1}</span></span>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Empty-state hint when no watchlist candidates */}
      {topBuyCandidates.length === 0 && tichLuyCount > 0 && (
        <Box sx={{
          mb: 2.5, p: 1.5, borderRadius: 1.5,
          bgcolor: '#eff6ff', border: '1px solid #93c5fd',
          display: 'flex', alignItems: 'center', gap: 1.2,
        }}>
          <Visibility sx={{ fontSize: 18, color: '#3b82f6' }} />
          <Typography sx={{ fontSize: '0.78rem', color: '#1e3a8a', fontWeight: 600 }}>
            {t('darvasBox.topBuy.noneToday', 'Hôm nay chưa có mã nào sát đỉnh hộp Darvas. Đây là tín hiệu thị trường đang co hẹp — chờ khi có mã breakout với volume lớn để vào lệnh.')}
          </Typography>
        </Box>
      )}

      {/* ── Tabs ── */}
      <Box sx={{ display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap' }}>
        {[
          { key: 'overview', label: t('darvasBox.tabs.overview'), count: scores.length, color: '#3b82f6' },
          { key: 'breakout', label: t('darvasBox.tabs.breakout'), count: breakoutReady.length, color: '#10b981' },
          { key: 'accumulation', label: t('darvasBox.tabs.accumulation'), count: accumulation.length, color: '#6366f1' },
          { key: 'watchlist', label: t('darvasBox.tabs.watchlist', 'Watchlist'), count: watchlistData.length, color: '#f59e0b' },
          { key: 'avoid', label: t('darvasBox.tabs.avoid'), count: avoidList.length, color: '#ef4444' },
        ].map(tb => (
          <Chip
            key={tb.key}
            label={<>{tb.label} <b style={{ marginLeft: 4 }}>{tb.count}</b></>}
            onClick={() => setTab(tb.key)}
            sx={{
              height: 30, fontSize: '0.75rem', fontWeight: 600, borderRadius: 2, cursor: 'pointer',
              bgcolor: tab === tb.key ? alpha(tb.color, 0.1) : '#f8fafc',
              color: tab === tb.key ? tb.color : '#64748b',
              border: '1px solid',
              borderColor: tab === tb.key ? alpha(tb.color, 0.3) : '#e2e8f0',
              '&:hover': { bgcolor: alpha(tb.color, 0.08), borderColor: alpha(tb.color, 0.25) },
            }}
          />
        ))}
      </Box>

      {/* ── Search + Sector Filter ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <TextField
          size="small"
          placeholder={t('darvasBox.searchPlaceholder')}
          value={searchTicker}
          onChange={(e) => setSearchTicker(e.target.value)}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> } }}
          sx={{
            width: 180,
            '& .MuiOutlinedInput-root': { fontSize: '0.8rem', height: 32, borderRadius: 1.5,
              '& fieldset': { borderColor: '#e2e8f0' },
              '&:hover fieldset': { borderColor: '#cbd5e1' },
            },
          }}
        />
        <Select
          size="small"
          value={sectorFilter}
          onChange={(e) => setSectorFilter(e.target.value)}
          displayEmpty
          sx={{
            minWidth: 180, height: 32, fontSize: '0.78rem', fontWeight: 600, borderRadius: 1.5,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
            '& .MuiSelect-select': { py: 0.5 },
          }}
        >
          <MenuItem value="" sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{t('darvasBox.allSectors')}</MenuItem>
          {sectorList.map(([code, name]) => (
            <MenuItem key={code} value={code} sx={{ fontSize: '0.78rem' }}>{name}</MenuItem>
          ))}
        </Select>
        {displayData.length > 0 && (
          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {displayData.length} {t('darvasBox.resultsCount')}
          </Typography>
        )}
      </Box>

      {/* ── Table ── */}
      {displayData.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CandlestickChart sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
          <Typography sx={{ color: '#94a3b8' }}>{t('darvasBox.empty')}</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ bgcolor: '#fff', borderRadius: 1.5, border: '1px solid #e2e8f0', overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={thSx}>#</TableCell>
                <TableCell sx={thSx}>{t('darvasBox.column.ticker')}</TableCell>
                <TableCell sx={thSx}>{t('darvasBox.column.close')}</TableCell>
                <TableCell sx={thSx}>{t('darvasBox.column.label')}</TableCell>
                <TableCell sx={thSx}>{t('darvasBox.column.action')}</TableCell>
                <TableCell sx={thSx} align="right">
                  <TableSortLabel active={sortCol === 'total_score'} direction={sortCol === 'total_score' ? sortDir : 'desc'} onClick={() => handleSort('total_score')}
                    sx={{ '& .MuiTableSortLabel-icon': { fontSize: 14 }, fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' }}>
                    {t('darvasBox.column.score')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={thSx}>
                  <TableSortLabel active={sortCol === 'position_in_zone'} direction={sortCol === 'position_in_zone' ? sortDir : 'desc'} onClick={() => handleSort('position_in_zone')}
                    sx={{ '& .MuiTableSortLabel-icon': { fontSize: 14 }, fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' }}>
                    {t('darvasBox.column.position')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={thSx} align="right">
                  <TableSortLabel active={sortCol === 'rr'} direction={sortCol === 'rr' ? sortDir : 'desc'} onClick={() => handleSort('rr')}
                    sx={{ '& .MuiTableSortLabel-icon': { fontSize: 14 }, fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' }}>
                    {t('darvasBox.column.rr')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={thSx}>{t('darvasBox.column.stopTarget')}</TableCell>
                <TableCell sx={thSx}>{t('darvasBox.column.reasons')}</TableCell>
                <TableCell sx={thSx}>{t('darvasBox.column.sector')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayData.map((s, i) => {
                const labelCfg = LABEL_COLORS[s.darvas_label] || LABEL_COLORS.SIDEWAY_TRUNG_TINH;
                const actionColor = ACTION_COLORS[s.darvas_action] || '#94a3b8';
                return (
                  <TableRow
                    key={`${s.ticker}-${i}`}
                    hover
                    onClick={() => setSelectedStock(s)}
                    sx={{
                      cursor: 'pointer',
                      '&:nth-of-type(even)': { bgcolor: '#fafbfc' },
                      '&:hover': { bgcolor: alpha('#3b82f6', 0.04) },
                    }}
                  >
                    <TableCell sx={{ ...tdSx, color: '#94a3b8', fontSize: '0.7rem' }}>{i + 1}</TableCell>
                    <TableCell sx={tdSx}>
                      <Box onClick={() => setSelectedStock(s)} sx={{ cursor: 'pointer' }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#0f172a', lineHeight: 1.3 }}>
                          {s.ticker}
                        </Typography>
                        {s.company_name && (
                          <Typography sx={{ fontSize: '0.68rem', color: '#64748b', lineHeight: 1.3 }}>
                            {s.company_name}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Box>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, fontFamily: 'monospace', color: '#1e293b' }}>
                          {s.last_close?.toLocaleString() || '—'}
                        </Typography>
                        {s.price_change_pct != null && (
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, fontFamily: 'monospace',
                            color: s.price_change_pct > 0 ? '#10b981' : s.price_change_pct < 0 ? '#ef4444' : '#94a3b8' }}>
                            {s.price_change_pct > 0 ? '+' : ''}{s.price_change_pct.toFixed(2)}%
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Chip label={t(`darvasBox.label.${s.darvas_label}`)} size="small"
                        sx={{ height: 22, fontSize: '0.65rem', fontWeight: 700, borderRadius: 1,
                          bgcolor: alpha(labelCfg.color, 0.12), color: labelCfg.color }} />
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: actionColor }}>
                        {t(`darvasBox.action.${s.darvas_action}`)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdSx} align="right">
                      <ScoreBar score={s.total_score} t={t} />
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <ZoneBar position={s.position_in_zone} t={t} />
                    </TableCell>
                    <TableCell sx={{ ...tdSx, fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 700 }} align="right">
                      {(() => { const rr = calcRR(s); if (rr === null) return '—'; const color = rr >= 2 ? '#059669' : rr >= 1 ? '#2563eb' : '#dc2626'; return <span style={{ color }}>{rr.toFixed(1)}</span>; })()}
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Box>
                        {s.stop_loss && (
                          <Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace', color: '#ef4444', fontWeight: 600, lineHeight: 1.4 }}>
                            SL: {Math.round(s.stop_loss).toLocaleString()}{s.risk_pct != null ? ` (−${s.risk_pct.toFixed(1)}%)` : ''}
                          </Typography>
                        )}
                        {s.target_1 && (
                          <Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace', color: '#10b981', fontWeight: 600, lineHeight: 1.4 }}>
                            T1: {Math.round(s.target_1).toLocaleString()}{s.last_close ? ` (+${((s.target_1 - s.last_close) / s.last_close * 100).toFixed(1)}%)` : ''}
                          </Typography>
                        )}
                        {!s.stop_loss && !s.target_1 && <Typography sx={{ fontSize: '0.7rem', color: '#cbd5e1' }}>—</Typography>}
                      </Box>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: '0.68rem', color: '#64748b', maxWidth: 220, whiteSpace: 'normal', lineHeight: 1.4 }}>
                        {(() => {
                          const lang = i18n.language === 'vi' ? 'vi' : 'en';
                          const raw = Array.isArray(s.reasons) ? s.reasons : [];
                          const list = raw
                            .map(r => (typeof r === 'string' ? r : (r[lang] || r.vi || r.en || '')))
                            .filter(Boolean);
                          return list.length ? list.slice(0, 2).join('; ') : '—';
                        })()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...tdSx, fontSize: '0.72rem', color: '#475569' }}>
                      {(i18n.language === 'vi' ? s.sector_name_vi : s.sector_name) || s.sector || '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Top per Label Cards ── */}
      {scores.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
          {[
            { key: 'breakout', title: t('darvasBox.topBreakout'), data: breakoutReady.slice(0, 8), color: '#10b981', icon: <TrendingUp sx={{ fontSize: 18 }} /> },
            { key: 'accumulation', title: t('darvasBox.topAccumulation'), data: accumulation.slice(0, 8), color: '#3b82f6', icon: <ShowChart sx={{ fontSize: 18 }} /> },
            { key: 'avoid', title: t('darvasBox.topAvoid'), data: avoidList.slice(0, 8), color: '#ef4444', icon: <Warning sx={{ fontSize: 18 }} /> },
          ].filter(g => g.data.length > 0).map(group => (
            <Box key={group.key} sx={{ flex: '1 1 280px', bgcolor: '#fff', borderRadius: 1.5, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1.25, borderBottom: '2px solid #e2e8f0', bgcolor: alpha(group.color, 0.04),
                display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ color: group.color, display: 'flex' }}>{group.icon}</Box>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: group.color }}>
                  {group.title}
                </Typography>
              </Box>
              {group.data.map((s, idx) => {
                const actionColor = ACTION_COLORS[s.darvas_action] || '#94a3b8';
                return (
                  <Box key={s.ticker} onClick={() => setSelectedStock(s)}
                    sx={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      px: 2, py: 0.75, cursor: 'pointer',
                      borderBottom: idx < group.data.length - 1 ? '1px solid #f1f5f9' : 'none',
                      '&:hover': { bgcolor: alpha(group.color, 0.02) },
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, width: 16, flexShrink: 0 }}>{idx + 1}</Typography>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>{s.ticker}</Typography>
                        {s.company_name && (
                          <Typography sx={{ fontSize: '0.68rem', color: '#64748b', lineHeight: 1.3 }}>
                            {s.company_name}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: actionColor }}>
                        {t(`darvasBox.action.${s.darvas_action}`)}
                      </Typography>
                      <ScoreBar score={s.total_score} t={t} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      )}

      {/* ── Detail Drawer ── */}
      <DetailDrawer
        open={!!selectedStock}
        onClose={() => setSelectedStock(null)}
        stock={selectedStock}
        t={t}
        i18n={i18n}
      />
    </Box>
  );
};

export default DarvasBox;
